import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { FullscreenTakeover } from "@jsq/ds/core";
import { Column } from "@jsq/ds/layouts";
import { useQueryParam } from "~/common/hooks/useQueryParams";
import { ProfileCreationModal } from "../../CaseDetailsDrawer/Profiles/common/AutoProfileCreationCard/ProfileCreationModal";
import CreateEntityForm from "../../CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateEntityForm";
import CreateIndividualForm from "../../CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateIndividualForm";
import { isEntityClientOnboardingCase } from "../../CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/utils/isEntityClientOnboardingCase";
import { useCurrentCaseContext } from "../../context/CurrentCaseContext";
import { ProfileCreationPreview } from "../../documents/ProfileCreationPreview";
import JointProfileForm from "../JointProfileForm";
import ProfileCategorySelector from "./ProfileCategorySelector";
import ProfileMethodSelector from "./ProfileMethodSelector";

interface Props {
  caseId: number;
  parentProfileId: number | undefined;
  onExit: () => void;
}

type FormCategory = "individual" | "entity";

const TITLES: Record<string, string> = {
  joint: "New joint ownership",
  individual: "Create individual",
  entity: "Create entity",
};

const buildMethodSearch = (search: string, category: FormCategory): string => {
  const params = new URLSearchParams(search);
  params.set("category", category);
  return `?${params.toString()}`;
};

const ProfileCreator = ({ caseId, parentProfileId, onExit }: Props) => {
  const history = useHistory();
  const match = useRouteMatch();
  const location = useLocation();
  const { caseType } = useCurrentCaseContext();
  const isOnboardingCase = isEntityClientOnboardingCase(caseType);
  const includeJoint = !isOnboardingCase;
  const [parentProfileIdParam] = useQueryParam("parentProfileId");
  const categoryParam = new URLSearchParams(location.search).get("category");

  const methodFormMatch = useRouteMatch<{ category: FormCategory }>(
    `${match.path}/method/:category`,
  );
  const jointMatch = useRouteMatch(`${match.path}/joint`);

  const isFormCategory = (value: string | undefined | null): value is FormCategory =>
    value === "individual" || value === "entity";

  // Prefer the query param, falling back to the route param on the manual-form paths.
  const methodCategory: FormCategory | null = isFormCategory(categoryParam)
    ? categoryParam
    : isFormCategory(methodFormMatch?.params.category)
      ? methodFormMatch.params.category
      : null;

  const resolveTitle = (): string => {
    if (jointMatch) return TITLES.joint;
    if (methodCategory) return TITLES[methodCategory];
    return "Create new profile";
  };

  const parsedParentProfileId = Number(parentProfileIdParam);
  const resolvedParentProfileId =
    parentProfileIdParam && Number.isFinite(parsedParentProfileId)
      ? parsedParentProfileId
      : parentProfileId;

  const navigateToSelector = () =>
    history.push({ pathname: match.url, search: location.search });

  const navigateToMethod = (category: FormCategory) => () =>
    history.push({
      pathname: `${match.url}/method`,
      search: buildMethodSearch(location.search, category),
    });

  const navigateBackToMethod = () => {
    const params = new URLSearchParams(location.search);
    params.delete("documentId");
    history.push({ pathname: `${match.url}/method`, search: `?${params.toString()}` });
  };

  const navigateToJoint = () =>
    history.push({ pathname: `${match.url}/joint`, search: location.search });

  const navigateToManualForm = () => {
    if (!methodCategory) return;
    history.push({
      pathname: `${match.url}/method/${methodCategory}`,
      search: location.search,
    });
  };

  const navigateToExtraction = () =>
    history.push({ pathname: `${match.url}/method/extraction`, search: location.search });

  const navigateToManualFormFromExtraction = () => {
    if (methodCategory) {
      navigateToManualForm();
    } else {
      navigateToSelector();
    }
  };

  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header title={resolveTitle()} onExit={onExit} />
      <Switch>
        <Route path={`${match.path}/method/extraction/preview`} exact>
          <ProfileCreationPreview
            parentProfileId={resolvedParentProfileId}
            onCancel={navigateBackToMethod}
            onComplete={onExit}
          />
        </Route>
        <Route path={`${match.path}/joint`}>
          <JointProfileForm
            caseId={caseId}
            parentProfileId={resolvedParentProfileId}
            onBack={navigateToSelector}
            onCompleted={onExit}
          />
        </Route>
        <Route path={`${match.path}/method/individual`}>
          <FullscreenTakeover.Body>
            <CreateIndividualForm
              caseId={caseId}
              backNavigationLabel="Choose a method"
              parentProfileId={resolvedParentProfileId}
              isOnboardingCase={isOnboardingCase}
              inAiExtractionMode
              navigateOnCancel={navigateToMethod("individual")}
              navigateOnCompletion={onExit}
            />
          </FullscreenTakeover.Body>
        </Route>
        <Route path={`${match.path}/method/entity`}>
          <FullscreenTakeover.Body>
            <CreateEntityForm
              caseId={caseId}
              backNavigationLabel="Choose a method"
              parentProfileId={resolvedParentProfileId}
              isOnboardingCase={isOnboardingCase}
              inAiExtractionMode
              navigateOnCancel={navigateToMethod("entity")}
              navigateOnCompletion={onExit}
            />
          </FullscreenTakeover.Body>
        </Route>
        <Route path={`${match.path}/method`}>
          {methodCategory ? (
            <FullscreenTakeover.Body>
              <Column size="md">
                <ProfileMethodSelector
                  title={isOnboardingCase ? "Add owner or controller" : undefined}
                  backLabel="Select an account type"
                  onBack={navigateToSelector}
                  onSelectManual={navigateToManualForm}
                  onSelectExtraction={navigateToExtraction}
                />
              </Column>
            </FullscreenTakeover.Body>
          ) : (
            <Redirect to={{ pathname: match.url, search: location.search }} />
          )}
          <Route path={`${match.path}/method/extraction`} exact>
            <ProfileCreationModal onManualCreate={navigateToManualFormFromExtraction} />
          </Route>
        </Route>
        <Route exact path={match.path}>
          <FullscreenTakeover.Body>
            <Column size="md">
              <ProfileCategorySelector
                heading="Create new owner or controller"
                title="Select an account type"
                backLabel="All owners and controllers"
                onBack={onExit}
                onSelectEntity={navigateToMethod("entity")}
                onSelectJoint={includeJoint ? navigateToJoint : undefined}
                onSelectIndividual={navigateToMethod("individual")}
              />
            </Column>
          </FullscreenTakeover.Body>
        </Route>
      </Switch>
    </FullscreenTakeover>
  );
};

export default ProfileCreator;
